/* =========================================================================
 * /dataPrivacy — privacy policy.
 *
 * Rewritten as part of the 2026 redesign — uses the shared LegalDoc layout
 * and updates the sensor + reward language to match the current pipeline
 * (microphone amplitude, not dB; rewards via in-area Bripplex campaigns,
 * not per-row revenue share).
 * ========================================================================= */

import { LegalDoc } from "pages/Docs/components/LegalDoc";

const SECTIONS = [
  {
    title: "Data we collect",
    body: (
      <>
        <p>The Bripplex app collects sensor data from your device, including:</p>
        <ul>
          <li>
            <strong>Location</strong> — latitude, longitude, altitude, speed, and bearing.
          </li>
          <li>
            <strong>Microphone</strong> — normalized amplitude only (how loud), never an audio
            sample. Conversations and ambient sound content stay on your device.
          </li>
          <li>
            <strong>Accelerometer</strong> — three-axis device acceleration.
          </li>
          <li>
            <strong>Gyroscope</strong> — three-axis rotation rate.
          </li>
          <li>
            <strong>Magnetometer</strong> — magnetic field strength.
          </li>
          <li>
            <strong>Device motion</strong> — orientation around the device&apos;s three axes.
          </li>
          <li>
            <strong>Compass</strong> — heading in degrees.
          </li>
          <li>
            <strong>Barometer</strong> — atmospheric pressure in hectopascals.
          </li>
          <li>
            <strong>Light</strong> — ambient illuminance.
          </li>
          <li>
            <strong>Carrier</strong> — cellular network name, type, and signal strength.
          </li>
        </ul>
        <p>
          The full sensor reference (with field-level schema and sample payloads) is at{" "}
          <a href="/docs/what">What data we collect</a>.
        </p>
      </>
    ),
  },
  {
    title: "Anonymity",
    body: (
      <p>
        All data is fully anonymized. We do not collect personally identifiable information — no
        name, email address, phone number, or persistent device identifiers. Devices are represented
        by an anonymous Firebase Auth UID with no link back to a real person.
      </p>
    ),
  },
  {
    title: "How data is used",
    body: (
      <p>
        Collected readings are grouped into 20-second location epochs, aggregated into 250 km
        contribution areas, and made available to research partners — universities, environmental
        agencies, and public-good organizations — as documented dataset slices. The end-to-end
        pipeline is described at <a href="/docs/how">How we collect the data</a>.
      </p>
    ),
  },
  {
    title: "Sharing and access",
    body: (
      <p>
        Researchers receive aggregated, area-scoped datasets — not individual device tracks. When
        Bripplex licenses data in a region, that region&apos;s contributors may be rewarded through
        Bripplex-run marketing campaigns (in-area giveaways, store credits, or charitable donations
        directed by the contributor community). No revenue is tied to any individual record.
      </p>
    ),
  },
  {
    title: "Security",
    body: (
      <p>
        Data is transmitted over TLS and stored in Google Cloud (Firebase). We apply encryption at
        rest, access controls, and routine audits. No internet-connected system is ever 100% secure,
        but we treat contributor data with the same standards as our own production systems.
      </p>
    ),
  },
  {
    title: "Your choices",
    body: (
      <p>
        Sessions in the app are explicit start/stop — nothing is collected unless you have initiated
        a contribution session. Individual sensors can be toggled at any time. To stop all
        collection, end the active session or uninstall the app.
      </p>
    ),
  },
  {
    title: "Data deletion",
    body: (
      <>
        <p>
          You can request that we delete all data associated with your device at any time. Because
          Bripplex stores contributions under an anonymous Firebase identifier — and never collects
          your name, email, phone, or any other PII — we need that identifier to find your data.
          It&apos;s shown in the app:
        </p>
        <ol>
          <li>Open the Bripplex app.</li>
          <li>
            Tap the <strong>⚙ Settings</strong> icon in the top-right of any tab.
          </li>
          <li>
            Under <strong>Device ID</strong>, tap <strong>Copy</strong>.
          </li>
          <li>
            Tap <strong>Request data deletion</strong> in the Support section — this opens your
            email app with a pre-filled message to{" "}
            <a href="mailto:contact@bripplex.com">contact@bripplex.com</a> containing your Device
            ID. Send it.
          </li>
        </ol>
        <p>
          Within seven days we will delete the device document, all per-epoch sensor records, and
          all raw batch files associated with that ID. We do not retain a copy.
        </p>
        <p>
          Aggregated regional data (250 km contribution areas, public leaderboard rankings) does not
          link back to individual contributors and is not affected by a deletion request.
        </p>
        <p>
          If you can&apos;t open the app — for example, you already uninstalled it — email{" "}
          <a href="mailto:contact@bripplex.com">contact@bripplex.com</a> from the address you want
          us to confirm the deletion to, describing your device and approximate contribution
          timeframe. We&apos;ll do our best to match and confirm.
        </p>
      </>
    ),
  },
  {
    title: "Changes to this policy",
    body: (
      <p>
        We may update this Privacy Policy from time to time. When we do, we will revise the
        &ldquo;Last updated&rdquo; date at the top of this page. Material changes will also be
        announced in-app.
      </p>
    ),
  },
  {
    title: "Contact",
    body: (
      <p>
        Questions or concerns about this policy or our data practices? Reach us at{" "}
        <a href="mailto:contact@bripplex.com">contact@bripplex.com</a>.
      </p>
    ),
  },
];

export const DataPrivacyTerms = () => (
  <LegalDoc
    eyebrow="Privacy"
    title="Privacy policy"
    lastUpdated="June 2026"
    lead={
      <>
        Bripplex collects sensor data from mobile devices for environmental and research purposes.
        This policy explains exactly what we collect, how it&apos;s anonymized, who receives it, and
        the control you have over the process.
      </>
    }
    sections={SECTIONS}
  />
);
